/**
 * Редактирование анкеты. Открывается тем же флагом modalQuestionnaire, что и
 * старая модалка QuestionnaireForm (из нижнего меню и сайдбара), но показывает
 * все разделы одной страницей — без шагов, чтобы можно было поправить одно поле.
 *
 * Блоки разделов переиспользуются из визарда (STEP_VIEWS), поэтому вид и правила
 * ввода совпадают с анкетой при регистрации.
 */
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';

import { LayoutContext } from '../../context';
import { APIGetProfile, APIUpdateBaseProfile } from '../../api';
import { APIPublicHobbies, APIPublicFields } from '../../api_auth';
import {
    AuthBackground, IconButton, IconClose, PrimaryButton, TextLink, Notice, Spinner,
} from '../Auth/ui';
import { STEP_VIEWS } from './OnboardingSteps';
import UserReferralCard from '../Referral/UserReferralCard';
import {
    INITIAL_ANSWERS, activeSteps, validateStep, answersFromProfile, isProfileDone,
    buildProfileFormData, compressImage, photoPickError, photoTooBig, mapRegisterError,
} from './onboardingConfig';

// Порядок разделов при редактировании: сначала «лицо» анкеты, потом детали
const SECTION_ORDER = [
    'photo', 'name', 'age', 'gender', 'coupleType', 'interest', 'purpose',
    'city', 'body', 'income', 'field', 'hobbies', 'partnerHobbies', 'about',
];

export default function ProfileEditScreen() {
    const navigate = useNavigate();
    const {
        modalQuestionnaire, setModalQuestionnaire, token, currentUser,
        profileStatus, setProfileUser, checkProfileStatus,
    } = useContext(LayoutContext);

    // Пока анкета не дозаполнена — ею занимается визард (OnboardingPage mode="profile"),
    // этот экран только для редактирования готовой.
    const open = Boolean(modalQuestionnaire?.open && isProfileDone(profileStatus));

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [a, setA] = useState(INITIAL_ANSWERS);
    const [photo, setPhoto] = useState(null);      // { url, file|null } — file только если меняли
    const [photoBusy, setPhotoBusy] = useState(false);
    const [profileId, setProfileId] = useState(null);
    const [lists, setLists] = useState({ hobbies: [], fields: [] });
    const [errors, setErrors] = useState({});
    const sectionRefs = useRef({});

    // Загрузка анкеты и справочников при открытии
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoading(true);
        setErrors({});
        setSaved(false);
        (async () => {
            const [profileRes, hobbiesRes, fieldsRes] = await Promise.allSettled([
                APIGetProfile(token), APIPublicHobbies(), APIPublicFields(),
            ]);
            if (!alive) return;
            if (profileRes.status === 'fulfilled' && profileRes.value) {
                const profile = profileRes.value;
                setProfileId(profile.id);
                setA(answersFromProfile(profile));
                setPhoto(profile.photo_file_path ? { url: profile.photo_file_path, file: null } : null);
            } else {
                setErrors({ form: 'Не удалось загрузить анкету. Попробуйте ещё раз' });
            }
            setLists({
                hobbies: hobbiesRes.status === 'fulfilled' ? (hobbiesRes.value?.hobbies || []) : [],
                fields: fieldsRes.status === 'fulfilled' ? (fieldsRes.value?.fields || []) : [],
            });
            setLoading(false);
        })();
        return () => { alive = false; };
    }, [open, token]);

    const sections = useMemo(() => {
        const active = new Set(activeSteps(a).map((s) => s.id));
        return SECTION_ORDER.filter((id) => active.has(id));
    }, [a]);

    const close = () => setModalQuestionnaire({ open: false });

    const patch = (p) => {
        setA((prev) => ({ ...prev, ...p }));
        setErrors((e) => (Object.keys(e).length ? {} : e));
    };

    const onPickPhoto = async (file) => {
        const pickError = photoPickError(file);
        if (pickError) { setErrors({ photo: pickError }); return; }
        setErrors({});
        setPhotoBusy(true);
        const ready = await compressImage(file);
        setPhotoBusy(false);
        if (photoTooBig(ready)) { setErrors({ photo: 'Фото слишком тяжёлое — выберите другое' }); return; }
        setPhoto((prev) => {
            if (prev?.file && prev.url) URL.revokeObjectURL(prev.url);
            return { file: ready, url: URL.createObjectURL(ready) };
        });
    };

    const scrollToSection = (id) => {
        sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const save = async () => {
        const failed = sections
            .map((id) => [id, validateStep(id, a, { photo })])
            .find(([, error]) => error);
        if (failed) {
            setErrors({ [failed[0]]: failed[1] });
            scrollToSection(failed[0]);
            return;
        }

        setSaving(true);
        setErrors({});
        try {
            await APIUpdateBaseProfile(token, profileId, buildProfileFormData(a, photo?.file || null));
            const fresh = await APIGetProfile(token);
            setProfileUser(fresh);
            if (currentUser?.id) await checkProfileStatus(token, currentUser.id);
            setSaved(true);
            setTimeout(close, 900);
        } catch (error) {
            const mapped = mapRegisterError(error, sections.map((id) => ({ id })));
            if (mapped.stepId) {
                setErrors({ [mapped.stepId]: mapped.formError });
                scrollToSection(mapped.stepId);
            } else {
                setErrors({ form: mapped.formError || 'Не удалось сохранить анкету' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {open ? (
                <motion.div className="lv-overlay"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}>
                    <AuthBackground>
                        <div className="lv-screen">
                            <header className="lv-onb__top">
                                <IconButton label="Закрыть" onClick={close}><IconClose /></IconButton>
                                <div className="lv-edit__title">Моя анкета</div>
                                <span className="lv-onb__count" aria-hidden="true" />
                            </header>

                            {loading ? (
                                <div className="lv-edit__loading"><Spinner size="lg" /></div>
                            ) : (
                                <>
                                    <div className="lv-edit__body">
                                        {errors.form ? <Notice tone="error">{errors.form}</Notice> : null}

                                        {sections.map((id) => {
                                            const view = STEP_VIEWS[id];
                                            const Content = view.Content;
                                            return (
                                                <Fragment key={id}>
                                                    {id === 'name' ? <UserReferralCard /> : null}
                                                    <section className="lv-card"
                                                             ref={(el) => { sectionRefs.current[id] = el; }}>
                                                        <h2 className="lv-card__title">{view.editTitle || view.title}</h2>
                                                        <div className="lv-content">
                                                            <Content
                                                                a={a} patch={patch} choose={patch} next={() => {}}
                                                                lists={lists} errors={errors} error={errors[id]}
                                                                photo={photo} photoBusy={photoBusy} onPickPhoto={onPickPhoto}
                                                            />
                                                        </div>
                                                    </section>
                                                </Fragment>
                                            );
                                        })}

                                        <TextLink small onClick={() => { close(); navigate('/quizzes'); }}>
                                            Мои тесты — подбор станет точнее →
                                        </TextLink>
                                    </div>

                                    <footer className="lv-onb__footer">
                                        <PrimaryButton onClick={save} loading={saving}>
                                            {saved ? 'Сохранено ✓' : (saving ? 'Сохраняем…' : 'Сохранить анкету')}
                                        </PrimaryButton>
                                    </footer>
                                </>
                            )}
                        </div>
                    </AuthBackground>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}
